import { useRef, useState } from 'react';
import type { BlockContent } from '../../../lib/blockContentSchemas';
import type { EditorAsset } from '../editor.types';
import { assetService } from '../../assets';
import { SampleArtwork } from './SampleArtwork';
import { cropGeometry, cropRatio, resizedCropRatio } from './imageCrop';

type Crop = NonNullable<BlockContent<'picture'>['crop']>;
export function PictureImage({content, asset, onCrop, square = false}: {content: BlockContent<'picture'>; asset?: EditorAsset; onCrop?: (crop: Crop) => void; square?: boolean}) {
  const corners = square ? 'rounded-none [&_.sample-artwork]:rounded-none' : 'rounded-site';
  const [loadedRatio, setLoadedRatio] = useState<number | null>(null);
  const ratio = loadedRatio ?? (asset?.width && asset.height ? asset.width / asset.height : 1.5);
  // A soft placeholder while the image loads; gone once it has, so a
  // transparent image (a logo, say) shows the page behind it, not a box.
  const placeholder = loadedRatio === null ? 'bg-site-highlight' : '';
  const drag = useRef<{x: number; y: number; crop: Crop; overflowX: number; overflowY: number} | null>(null);
  const resize = useRef<{x: number; y: number; width: number; height: number; crop: Crop} | null>(null);
  const crop = content.crop;
  const geometry = crop ? cropGeometry(ratio, cropRatio(crop.ratio, crop.customRatio ?? ratio), crop.zoom, crop.x, crop.y) : null;
  const image = asset ? <img alt={content.alt || asset.alt_text || ''} src={assetService.getPublicUrl(asset.storage_path)} draggable={false} onLoad={e => setLoadedRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)} className={crop ? 'h-full w-full' : `max-h-[32rem] w-full ${corners} ${placeholder} ${content.fit === 'contain' ? 'object-contain' : content.fit === 'fill' ? 'object-fill' : 'object-cover'}`} /> : <div className={corners}><SampleArtwork kind={content.sample!} /></div>;
  if (!crop || !geometry) return image;
  return <div className={`relative w-full overflow-hidden ${corners} ${placeholder} ${onCrop ? "touch-none cursor-move" : ""}`} style={{aspectRatio: cropRatio(crop.ratio, crop.customRatio ?? ratio)}}
    onPointerDown={e => {
      if (!onCrop || e.button !== 0) return;
      e.preventDefault(); e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      const rect = e.currentTarget.getBoundingClientRect();
      drag.current = {x: e.clientX, y: e.clientY, crop, overflowX: rect.width * (geometry.width / 100 - 1), overflowY: rect.height * (geometry.height / 100 - 1)};
    }}
    onPointerMove={e => {
      const start = drag.current;
      if (!start || !onCrop) return;
      const clamp = (value: number) => Math.min(100, Math.max(0, value));
      onCrop({...start.crop, x: start.overflowX > 0 ? clamp(start.crop.x - (e.clientX - start.x) / start.overflowX * 100) : start.crop.x, y: start.overflowY > 0 ? clamp(start.crop.y - (e.clientY - start.y) / start.overflowY * 100) : start.crop.y});
    }} onPointerUp={() => {drag.current = null;}} onPointerCancel={() => {drag.current = null;}}>
    <div className="absolute" style={{width: `${geometry.width}%`, height: `${geometry.height}%`, left: `${geometry.left}%`, top: `${geometry.top}%`}}>{image}</div>
    {onCrop && crop.ratio === 'free' && <button
      type="button"
      aria-label="Resize freeform crop"
      title="Drag to resize. Use arrow keys to adjust the crop shape."
      className="absolute bottom-0 right-0 z-10 grid size-9 cursor-nwse-resize touch-none place-items-center rounded-tl-lg bg-hazumu-white text-hazumu-ink shadow-sm focus-visible:outline-2 focus-visible:outline-hazumu-terracotta"
      onPointerDown={e => {
        if (e.button !== 0) return;
        e.preventDefault(); e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const rect = e.currentTarget.parentElement!.getBoundingClientRect();
        resize.current = {x: e.clientX, y: e.clientY, width: rect.width, height: rect.height, crop};
      }}
      onPointerMove={e => {
        if (!resize.current) return;
        e.stopPropagation();
        const start = resize.current;
        onCrop({...start.crop, customRatio: resizedCropRatio(start.width, start.height, e.clientX - start.x, e.clientY - start.y)});
      }}
      onPointerUp={e => {e.stopPropagation(); resize.current = null;}}
      onPointerCancel={() => {resize.current = null;}}
      onKeyDown={e => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
        e.preventDefault(); e.stopPropagation();
        const change = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 0.05 : -0.05;
        onCrop({...crop, customRatio: Math.min(5, Math.max(0.2, (crop.customRatio ?? 1) + change))});
      }}
    ><span aria-hidden="true">↘</span></button>}
    {onCrop && <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">{Array.from({length: 9}, (_, i) => <span key={i} className="border border-hazumu-white/30" />)}</div>}
  </div>;
}
